import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BillGroup, BillFile } from '../../models';
import { BillTypeBadgeComponent } from '../shared/bill-type-badge.component';
import { normalizeAccountNumber } from '../../utils/bill-utils';
import { formatBillDate } from '../../utils/date-format.util';

interface PairCardView {
    name: string;
    amount: string;
    fileName: string;
}

@Component({
  selector: 'app-paired-bill-resolver',
  standalone: true,
  imports: [CommonModule, BillTypeBadgeComponent],
  templateUrl: './paired-bill-resolver.component.html',
  styleUrl: './paired-bill-resolver.component.scss'
})
export class PairedBillResolverComponent implements OnChanges {
    @Input() group!: BillGroup;
    @Output() resolve = new EventEmitter<'confirm' | 'unpair' | 'pair' | 'close'>();

    isRejected = false;
    subtitle = '';
    utilityCard: PairCardView = { name: '-', amount: '-', fileName: '' };
    supplierCard: PairCardView = { name: '-', amount: '-', fileName: '' };
    combinedAmount = '';

    ngOnChanges() {
        if (!this.group) return;

        this.isRejected = !!this.group.rejected;
        const currency = this.group.utilityBill?.data?.currency || 'USD';
        const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency });

        this.utilityCard = this.buildCard(this.group.utilityBill, this.group.utilityBill?.data?.utilityName, fmt);
        this.supplierCard = this.buildCard(this.group.supplierBill, this.group.supplierBill?.data?.providerName, fmt);
        this.combinedAmount = fmt.format(this.group.combinedAmount);

        const period = `${formatBillDate(this.group.billingPeriod.start)} - ${formatBillDate(this.group.billingPeriod.end)}`;
        const shared = this.sharedAccountNumber();
        this.subtitle = shared ? `Same account ${shared}, ${period}` : period;
    }

    private buildCard(bill: BillFile | null, name: string | undefined, fmt: Intl.NumberFormat): PairCardView {
        return {
            name: name || '-',
            amount: bill?.data?.totalAmount != null ? fmt.format(bill.data.totalAmount) : '-',
            fileName: bill?.file?.name || ''
        };
    }

    private sharedAccountNumber(): string | null {
        const u = this.group.utilityBill?.data;
        const s = this.group.supplierBill?.data;
        if (!u || !s) return null;

        const collect = (d: typeof u) => [
            d.utilityAccountNumber, d.utilityAccountNumber2, d.utilityAccountNumber3,
            ...(d.additionalIdentifiers || []).map(id => id.value)
        ].filter(Boolean) as string[];

        const supplierNormalized = collect(s).map(normalizeAccountNumber);
        const match = collect(u).find(v => {
            const n = normalizeAccountNumber(v);
            return n.length >= 3 && supplierNormalized.includes(n);
        });
        return match || null;
    }

    onClose() {
        this.resolve.emit('close');
    }

    onConfirm() {
        this.resolve.emit('confirm');
    }

    onUnpair() {
        this.resolve.emit('unpair');
    }

    onPair() {
        this.resolve.emit('pair');
    }
}
